import { isClanPreloaded } from '~/helpers/clan';
import { userRolesWithBuildPermission } from '~/roles';
import { currentClanSelector, currentAccountSelector } from '~/store/selectors/currentAccountSelector';
import { createAppSelector } from '~/store/utils/createSelector';

import type { RootState } from '~/store';

// ReducerSupply selectors
const reducerSupplySelector = (state: RootState) => state.ReducerSupply;

export const supplyIsSidebarOpenedSelector = (state: RootState) => state.ReducerSupply.isSidebarOpened;
export const supplySelectedBuildingIdSelector = (state: RootState) => state.ReducerSupply.selectedBuildingId;
export const supplyBuildingInProgressSelector = (state: RootState) => state.ReducerSupply.buildingInProgress;

export const supplyBuildingsSelector = (state: RootState) => state.ReducerSupply.buildings;

export const supplyBuildingTreasurySelector = createAppSelector(supplyBuildingsSelector, (supplyBuildings) => {
  return supplyBuildings?.treasury;
});

export const supplyHasBuildPermissionSelector = createAppSelector(currentAccountSelector, (currentAccount) => {
  if (!currentAccount?.clanId || !currentAccount?.roleName) {
    return false;
  }

  return userRolesWithBuildPermission.includes(currentAccount.roleName);
});

export const supplyAccumulativeResSelector = createAppSelector(currentClanSelector, (currentClan) => {
  if (!currentClan || isClanPreloaded(currentClan)) {
    return 0;
  }

  return currentClan?.accumulativeResource || 0;
});

export const supplyPersonalResSelector = createAppSelector(currentClanSelector, (currentClan) => {
  if (!currentClan || isClanPreloaded(currentClan)) {
    return 0;
  }

  return currentClan?.personalResource || 0;
});

export const supplyViewSelector = createAppSelector(reducerSupplySelector, (ReducerSupply) => {
  return {
    buildings: ReducerSupply.buildings,
    confirmBuildingId: ReducerSupply.confirmBuildingId,
    isSidebarOpened: ReducerSupply.isSidebarOpened,
    mapHost: ReducerSupply.mapHost,
    prevSelectedBuildingId: ReducerSupply.prevSelectedBuildingId,
    selectedBuildingId: ReducerSupply.selectedBuildingId,
    tempHoverBuildingId: ReducerSupply.tempHoverBuildingId,
    tempHoverBuildingLevel: ReducerSupply.tempHoverBuildingLevel,
  };
});

export const supplyMapSelector = createAppSelector(reducerSupplySelector, (ReducerSupply) => {
  return {
    backgroundUrl: ReducerSupply.backgroundUrl,
    buildings: ReducerSupply.buildings,
    confirmBuildingId: ReducerSupply.confirmBuildingId,
    edgesVisible: ReducerSupply.edgesVisible,
    highlightDelay: ReducerSupply.highlightDelay,
    highlightedBuildingId: ReducerSupply.highlightedBuildingId,
    initialZoom: ReducerSupply.initialZoom,
    isMapBlured: ReducerSupply.isMapBlured,
    isSidebarOpened: ReducerSupply.isSidebarOpened,
    prevPosition: ReducerSupply.prevPosition,
    maxZoom: ReducerSupply.maxZoom,
    minZoom: ReducerSupply.minZoom,
    position: ReducerSupply.position,
    selectedBuildingId: ReducerSupply.selectedBuildingId,
    tempHoverBuildingId: ReducerSupply.tempHoverBuildingId,
    tempHoverBuildingLevel: ReducerSupply.tempHoverBuildingLevel,
    zoom: ReducerSupply.zoom,
  };
});

const supplyZoomStateSelector = createAppSelector(reducerSupplySelector, (ReducerSupply) => {
  return {
    zoom: ReducerSupply.zoom,
    initialZoom: ReducerSupply.initialZoom,
    minZoom: ReducerSupply.minZoom,
    maxZoom: ReducerSupply.maxZoom,
  };
});

export const supplyisMapZoomedSelector = createAppSelector(
  supplyZoomStateSelector,
  ({ initialZoom, zoom, minZoom, maxZoom }) => {
    return (minZoom !== maxZoom && zoom === maxZoom) || (minZoom <= initialZoom && initialZoom < zoom);
  },
);
